/** Human-readable text for game log entries (used by the client's hand history). */

import { type Seat, SUIT_NAMES, SUIT_SYMBOLS, cardLabel } from './cards.js';
import type { HandResult, LogEntry } from './game.js';

export function describeResult(r: HandResult, nameOf: (s: Seat) => string, teamName: (t: 0 | 1) => string): string {
  const makers = teamName(r.makerTeam);
  const scorer = teamName(r.scoringTeam);
  const pts = `${r.points} point${r.points === 1 ? '' : 's'}`;
  switch (r.outcome) {
    case 'march':
      return `${r.makerAlone ? `${nameOf(r.maker)} went alone and took` : `${makers} took`} all 5 tricks — ${scorer} +${pts}.`;
    case 'made':
      return `${makers} made it with ${r.makerTricks} tricks — +${pts}.`;
    case 'euchre':
      return `${makers} were euchred${r.defenderAlone !== null ? ` by ${nameOf(r.defenderAlone)} defending alone` : ''} — ${scorer} +${pts}.`;
  }
}

export function describeLog(e: LogEntry, nameOf: (s: Seat) => string, teamName: (t: 0 | 1) => string): string {
  switch (e.t) {
    case 'deal':
      return `${nameOf(e.dealer)} dealt. ${cardLabel(e.upCard)} turned up.`;
    case 'redeal_called':
      return `${nameOf(e.seat)} had no ace, no face — redeal.`;
    case 'farmer_swap':
      return `${nameOf(e.seat)} played a farmer's hand and swapped three cards with the kitty.`;
    case 'pass':
      return `${nameOf(e.seat)} passed${e.round === 2 ? ' (round 2)' : ''}.`;
    case 'order_up':
      return `${nameOf(e.seat)} ordered up ${cardLabel(e.upCard)}${e.alone ? ' and is going alone' : ''}.`;
    case 'call_trump':
      return `${nameOf(e.seat)} called ${SUIT_NAMES[e.suit]} ${SUIT_SYMBOLS[e.suit]}${e.alone ? ' and is going alone' : ''}.`;
    case 'defend_alone':
      return `${nameOf(e.seat)} is defending alone.`;
    case 'pick_up':
      return `${nameOf(e.seat)} picked up ${cardLabel(e.upCard)}.`;
    case 'discarded':
      return `${nameOf(e.seat)} discarded.`;
    case 'trick':
      return `Trick ${e.trick.number}: ${e.trick.cards.map((p) => `${nameOf(p.seat)} ${cardLabel(p.card)}`).join(', ')} — ${nameOf(e.trick.winner)} wins.`;
    case 'thrown_in':
      return 'Everyone passed. The hand is thrown in and the deal passes left.';
    case 'result':
      return `${describeResult(e.result, nameOf, teamName)} Score ${e.scores[0]}–${e.scores[1]}.`;
  }
}
